// The OPG trades "skill" seam (CR-20260710-001 — the phase-1/phase-2 boundary, spec §13).
//
// The trade-selection brain is Chris's phase 2. The MOCK implements this interface today;
// the REAL skill swaps the DI provider (see OptionsPricingGridModule) with NO change to
// the UI/orchestrator. Transport (backend command + ESS push a la Hedge vs a frontend
// client) is deliberately undecided — this token is the swap seam either way.
//
// COURIER PRINCIPLE (§6): the host passes the questionnaire VERBATIM and reflects the
// result VERBATIM — no filtering, re-ranking, resizing, or card caps host-side. Any
// result cap (<=5 today) lives in the mock generator ONLY.

import {InjectionToken} from '@angular/core';
import {
    MarketConditionOverride,
    OpgDateSelection,
    OpgTradeContent,
    SideInputs,
    TabKind
} from './opg-copilot-domain';
import {ExpectedMoveRangeModel} from '../expected-move/expected-move.service';
import {OpgEsContractCandidate} from './opg-copilot-facade';

// Panel context snapshot sent WITH the questionnaire so the skill sees what the trader
// sees. expirations are the VISIBLE-filtered 'yyyy-MM-dd' keys (contract-level rule —
// non-visible expirations must never be suggested; a returned trade's `expiration` MUST be
// one of them). selectedExpiration is the panel's currently-loaded expiration — CONTEXT
// (the loaded-date default, the currently-shown chain); under A5 trades NAME their own
// `expiration` rather than being relative to this. Both ATM values are carried; WHICH one
// an atmOffset run honors is the questionnaire's own priceRange.anchor — an explicit user
// input since A3 (owner smoke 2026-07-10; supersedes "Chris's deferred call", old §15 open #3).
export interface OpgSkillContext {
    // ADR-037 (CR-20260716-001) wire amendments — panel STATE the skill cannot know, which is
    // exactly the class of fact the context exists for (courier rule):
    //   underlying — the panel's symbol (orchestrator readSymbolSafe()); an EMPTY symbol fails
    //   pre-send validation -> preSendBlocked (the 2026-07-14 "underlying deliberately NOT a
    //   member" ruling concerns the stale-grid FINGERPRINT, not this wire).
    //   panelInstanceId — opaque per-panel-instance id (session-lifetime); the backend registry
    //   scopes run supersede to (terminalId, panelInstanceId) so two OPG panels on one terminal
    //   never kill each other's runs (ADR-033). The TRANSPORT hoists it to the op's top level
    //   (C-1) — it does not ride the wire context.
    underlying: string;
    panelInstanceId: string;
    expirations: string[];
    selectedExpiration: string | null;
    strikeLadder: number[];
    chosenAtm: number;
    dynamicAtm: number;
    defaultQty: number;
    // CR-20260924-002 (ES mode, Hedge Matrix host only): translate the answer onto the ES contract.
    // Absent/false = today's run. esContracts = the candidates, nearest first (see the facade).
    esMode?: boolean;
    esContracts?: OpgEsContractCandidate[] | null;
}

// One Run carries the whole tab: Calls/Puts send their one side; Calls & Puts sends BOTH
// sides under the ONE shared date (one Run button per tab, §5).
// CONTRACT (hide-risk-for-follow-through, meeting 2026-07-15 pkg-03): a side whose coverage
// is purely follow-through (domain isFollowThroughOnly — main mode, or ATM Offset with the
// Follow-Through sub) carries maxRisk: null. The risk question does not exist there (risk
// levels size the brake; a follow-through has none — owner 00:43–00:44), so the skill must
// not expect a risk answer for such a side and must never label its results with one. A
// 'both' sub still carries the answer — it shapes the RANGE family only (owner 00:28).
export interface OpgSkillRequest {
    tab: TabKind;
    date: OpgDateSelection;
    sides: { calls?: SideInputs; puts?: SideInputs };
    context: OpgSkillContext;
    // CR-20260722-002. User-supplied market conditions keyed by 'yyyy-MM-dd', carrying ONLY dates
    // the user edited — absent/empty means measure everything. OPTIONAL so the mock skill and the
    // existing specs remain valid without change.
    marketOverrides?: { [dateKey: string]: MarketConditionOverride };
    // CR-20260727-001. The user's App Settings -> Expected Move Settings MODEL, so an
    // 'expectedRange' price range resolves under the model the panel is actually showing instead of
    // always the ATM straddle.
    //
    // THE MODEL, NEVER THE VALUE. Resolving the expected range frontend-side is prohibited (owner
    // ruling CR-20260715-001, restated in ADR-035's rejected-alternatives list) — the skill still
    // computes the boundary from its own market facts. This only says WHICH model to compute under.
    //
    // OPTIONAL: absent means 'ATM Straddle', which is byte-for-byte the pre-CR behaviour, so the
    // mock skill and the existing specs remain valid without change.
    expectedRangeModel?: { model: ExpectedMoveRangeModel; customPercent: number | null };
}

// Per side: ONE ranked LIST of trades (index 0 = best), which in a Date Range may each
// name a DIFFERENT expiration (FR-14 removed the old result-shape discriminator — there is
// exactly one result shape now). A side with nothing to suggest is simply ABSENT — a
// legitimately-empty result, not an error (§6).
export interface OpgSkillSideResult {
    trades: OpgTradeContent[];
}

export interface OpgSkillResult {
    calls?: OpgSkillSideResult;
    puts?: OpgSkillSideResult;
    // CR-20260902-001: the skill's own explanation for an ABSENT side (one line per affected
    // date). Absent = the generic "no trades" message is all there is. Rendered VERBATIM (courier).
    callsNote?: string;
    putsNote?: string;
}

export interface IOpgTradesSkill {
    // Resolves with grid-expressible trades (§7). Empty result = valid "no suggestion";
    // a rejected promise / stall is a terminal failure (the session backstop covers the
    // stall — stuck !== empty, §11).
    run(request: OpgSkillRequest): Promise<OpgSkillResult>;
}

export const OPG_TRADES_SKILL = new InjectionToken<IOpgTradesSkill>('OPG_TRADES_SKILL');
